package com.devi3east.bashbuddy.footer.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp

@Composable
fun PrivacyPolicyScreen(
    onBack: () -> Unit,
    date: String
) {
    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
    ) {
        val maxWidthDp = maxWidth
        val targetWidth = when {
            maxWidthDp < 600.dp -> maxWidthDp
            maxWidthDp < 900.dp -> 600.dp
            else -> 720.dp
        }

        val contentModifier = Modifier
            .widthIn(max = targetWidth)
            .align(Alignment.TopCenter)

        Column(
            modifier = contentModifier
                .padding(horizontal = 16.dp)
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(vertical = 8.dp)
            ) {
                IconButton(
                    onClick = onBack,
                    modifier = Modifier.align(Alignment.CenterStart)
                ) {
                    Icon(
                        imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                        contentDescription = "Back",
                        tint = MaterialTheme.colorScheme.onSurface
                    )
                }

                Row(
                    modifier = Modifier.align(Alignment.Center),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center
                ) {
                    Text(
                        text = "Privacy Policy",
                        style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                        color = MaterialTheme.colorScheme.onSurface
                    )
                }
            }

            Text(
                text = "Last updated: $date",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )

            // 1. Introduction
            Text(
                text = "1. Introduction",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = "This Privacy Policy explains how bash buddy™ (“bash buddy”, “we”, “us”, or “our”) collects, uses, and shares information when you access or use our platform, applications, or related services (collectively, the “Platform”). We are committed to protecting your privacy and handling your data responsibly.",
                style = MaterialTheme.typography.bodyMedium
            )

            // 2. Information We Collect
            Text(
                text = "2. Information We Collect",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("We collect only the information necessary to operate and support the Platform. This may include:\n\n")
                    append("- Account information such as name, email address, and basic profile details.\n")
                    append("- Organization verification information provided by event organizers to confirm ownership or authority over listed events.\n")
                    append("- Event participation data, such as attendance or registration information, where applicable.\n")
                    append("- Technical information such as device type, application version, and limited usage analytics to help us improve reliability and performance.\n")
                },
                style = MaterialTheme.typography.bodyMedium
            )

            // 3. How We Use Information
            Text(
                text = "3. How We Use Information",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("We use collected information solely to:\n\n")
                    append("- Verify ownership or administrative control of events and organizations.\n")
                    append("- Enable communication between event organizers and participants.\n")
                    append("- Display event information and participation details within the Platform.\n")
                    append("- Maintain platform security, prevent misuse, and improve system performance.\n")
                },
                style = MaterialTheme.typography.bodyMedium
            )

            // 4. Sharing of Information
            Text(
                text = "4. Sharing of Information",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("We do not sell your personal information.\n\n")
                    append("Information may be shared only in the following limited circumstances:\n\n")
                    append("- With event organizers: Participant information is shared with the organization hosting an event for purposes of managing participation, communications, and event operations.\n")
                    append("- With service providers: We may share limited data with trusted service providers who assist us in operating the Platform (such as hosting, analytics, or email delivery), subject to confidentiality obligations.\n")
                    append("- Legal requirements: We may disclose information if required to do so by law or in response to valid legal requests.\n")
                },
                style = MaterialTheme.typography.bodyMedium
            )

            // 5. Data Retention
            Text(
                text = "5. Data Retention",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = "We retain personal information only for as long as necessary to fulfill the purposes described in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements.",
                style = MaterialTheme.typography.bodyMedium
            )

            // 6. Data Security
            Text(
                text = "6. Data Security",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = "We implement reasonable administrative, technical, and organizational measures designed to protect personal information against unauthorized access, loss, misuse, or alteration. However, no system can be guaranteed to be completely secure.",
                style = MaterialTheme.typography.bodyMedium
            )

            // 7. Your Rights
            Text(
                text = "7. Your Rights",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("Depending on your jurisdiction, you may have rights to access, correct, or request deletion of your personal information. Requests may be subject to verification and legal limitations.\n\n")
                    append("You may contact us using the information below to exercise applicable rights.")
                },
                style = MaterialTheme.typography.bodyMedium
            )

            // 8. Children’s Privacy
            Text(
                text = "8. Children’s Privacy",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = "The Platform is not directed to children under the age of 13, and we do not knowingly collect personal information from children. If we become aware that such information has been collected, we will take appropriate steps to delete it.",
                style = MaterialTheme.typography.bodyMedium
            )

            // 9. Changes to this Policy
            Text(
                text = "9. Changes to This Privacy Policy",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = "We may update this Privacy Policy from time to time. When we do, we will revise the “Last updated” date. Continued use of the Platform after changes indicates acceptance of the updated policy.",
                style = MaterialTheme.typography.bodyMedium
            )

            // 10. Contact
            Text(
                text = "10. Contact Us",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("If you have questions about this Privacy Policy or our data practices, please contact us at:\n\n")
                    append("Email: support@bash-buddy.com\n")
                },
                style = MaterialTheme.typography.bodyMedium
            )

            Spacer(modifier = Modifier.padding(bottom = 48.dp))
        }
    }
}